import React from "react";
import {Button} from "primereact/button";
import {Card} from "primereact/card";
import {Chip} from "primereact/chip";
import {Dropdown} from "primereact/dropdown";
import {Sidebar} from "primereact/sidebar";
import {Tag} from "primereact/tag";
import Icon from "../../../system/components/icon";
import {useTranslate} from "../../../system/useTranslate";

const dropdownPt = {input: () => ({className: "text-xs"}), item: () => ({className: "text-xs"})};

const money = (value) => Number(value || 0).toLocaleString("fa-IR");

const memberName = (membership) => membership?.member?.fullname
    || membership?.member?.username
    || membership?.fullname
    || membership?.username
    || "";

const toDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    const numeric = Number(value);
    if (Number.isFinite(numeric) && String(value).trim() !== "") {
        return new Date(numeric < 100000000000 ? numeric * 1000 : numeric);
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDate = (value, withTime = false) => {
    const date = toDate(value);
    if (!date) return null;
    return withTime ? date.toLocaleString("fa-IR") : date.toLocaleDateString("fa-IR");
};

const statusSeverity = (deal) => deal?.status === "open" ? "info" : deal?.result === "won" ? "success" : "danger";

const eventIcon = (type = "") => {
    if (type.includes("won")) return "mi mi:emoji_events";
    if (type.includes("lost")) return "mi mi:cancel";
    if (type.includes("reopen")) return "mi mi:refresh";
    if (type.includes("stage")) return "mi mi:swap_horiz";
    if (type.includes("task")) return "mi mi:task_alt";
    if (type.includes("note")) return "mi mi:edit_note";
    if (type.includes("file")) return "mi mi:attach_file";
    if (type.includes("product")) return "mi mi:inventory_2";
    return "mi mi:history";
};

const taskDescription = (task) => task?.description || task?.content || task?.details || null;
const taskDueDate = (task) => task?.dueDate || task?.due_date || task?.deadline || null;
const taskAssignee = (task) => memberName(task?.assignee || task?.owner || task?.assignedTo);

function DealMetric({label, value, primary = false}) {
    return <div className="flex-1 min-w-10rem surface-50 border-round-lg p-2 flex flex-column gap-1">
        <span className="text-xs text-500">{label}</span>
        <strong className={`text-sm ${primary ? "text-primary" : "text-800"}`}>{value}</strong>
    </div>;
}

function NextAction({action, onCreateTask}) {
    const translate = useTranslate();
    const dueDate = toDate(taskDueDate(action));
    return <div className="surface-50 border-1 border-100 border-round-xl p-3 flex flex-column gap-3" onClick={(event) => event.stopPropagation()}>
        <div className="flex justify-content-between align-items-start gap-3">
            <div className="flex flex-column gap-1 min-w-0">
                <span className="text-xs text-500">{translate("crm.customer.next-action")}</span>
                <strong className="text-sm text-800 white-space-normal overflow-hidden">{action?.title || translate("crm.customer.no-next-action")}</strong>
            </div>
            <Tag rounded severity={action ? "info" : "secondary"} value={translate("crm.deal.task-kind")}/>
        </div>
        {action && <>
            {taskDescription(action) && <span className="text-xs text-600 line-height-3 white-space-normal">{taskDescription(action)}</span>}
            <div className="flex flex-wrap align-items-center gap-3 text-xs text-500">
                {dueDate && <span className="inline-flex align-items-center gap-1"><Icon icon="mi mi:calendar_today text-sm"/>{dueDate.toLocaleDateString("fa-IR")}</span>}
                {dueDate && <span className="inline-flex align-items-center gap-1"><Icon icon="mi mi:schedule text-sm"/>{dueDate.toLocaleTimeString("fa-IR", {hour: "2-digit", minute: "2-digit"})}</span>}
                {taskAssignee(action) && <span className="inline-flex align-items-center gap-1"><Icon icon="mi mi:person text-sm"/>{taskAssignee(action)}</span>}
            </div>
        </>}
        {onCreateTask && <div className="flex justify-content-end">
            <Button type="button" outlined size="small" className="text-xs gap-2" icon={<Icon icon="mi mi:add_task text-base"/>} label={translate("crm.deal.create-task")} onClick={onCreateTask}/>
        </div>}
    </div>;
}

export function CurrentDealCard({
    deal,
    openDeals = [],
    nextAction,
    stages = [],
    canCreate = false,
    canUpdate = false,
    onCreate,
    onSelectDeal,
    onDetails,
    onChangeStage,
    onCreateTask,
}) {
    const translate = useTranslate();
    const activeStageIndex = Math.max(0, stages.indexOf(deal?.stage));
    const openCount = translate("crm.deal.open-count").replace("{{count}}", openDeals.length.toLocaleString("fa-IR"));

    return <Card className="surface-card border-1 border-200 border-round-xl shadow-none overflow-hidden" pt={{body: {className: "p-0"}, content: {className: "p-0"}}}>
        <div className="p-3 border-bottom-1 border-100 flex flex-column md:flex-row justify-content-between align-items-start md:align-items-center gap-2">
            <div className="flex align-items-center gap-2">
                <strong className="text-base text-800">{translate("crm.deal.current")}</strong>
                <Tag rounded severity="warning" value={openCount}/>
            </div>
            <div className="flex flex-wrap align-items-center gap-2">
                {openDeals.length > 1 && <Dropdown value={deal?.documentId} options={openDeals.map((item) => ({label: item.title, value: item.documentId}))} onChange={(event) => onSelectDeal?.(event.value)} className="text-xs" panelClassName="rtl text-xs" pt={dropdownPt}/>} 
                {deal && <Button type="button" outlined size="small" className="text-xs gap-2" icon={<Icon icon="mi mi:visibility text-base"/>} label={translate("crm.deal.details")} onClick={() => onDetails?.(deal)}/>} 
                {canCreate && <Button type="button" size="small" className="text-xs gap-2" icon={<Icon icon="mi mi:add text-base"/>} label={translate("crm.deal.create")} onClick={onCreate}/>} 
            </div>
        </div>
        <div className="p-3">
            {!deal ? <div className="surface-50 border-1 border-100 border-round-xl p-5 flex flex-column align-items-center text-center gap-2">
                <Icon icon="mi mi:handshake text-4xl text-300" size={38}/>
                <strong>{translate("crm.deal.empty")}</strong>
                <span className="text-xs text-500">{translate("crm.deal.empty-description")}</span>
            </div> : <div className="border-1 border-200 border-round-xl p-3 flex flex-column gap-3 cursor-pointer" role="button" tabIndex={0} onClick={() => onDetails?.(deal)} onKeyDown={(event) => {if (["Enter", " "].includes(event.key)) onDetails?.(deal);}}>
                <div className="flex flex-column md:flex-row justify-content-between align-items-start gap-3">
                    <div className="flex flex-column gap-1 min-w-0">
                        <small className="text-400">{deal.documentId || `#${deal.id}`}</small>
                        <strong className="text-lg text-900 white-space-normal overflow-hidden">{deal.title}</strong>
                        <strong className="text-primary text-base">{money(deal.dealValue)}</strong>
                    </div>
                    <div className="surface-50 border-round-lg p-2 flex flex-column gap-1 min-w-10rem">
                        <span className="text-xs text-500">{translate("crm.deal.owner")}</span>
                        <strong className="text-xs text-800">{memberName(deal.owner) || translate("crm.value.unassigned")}</strong>
                    </div>
                </div>

                <div className="flex flex-column gap-2" onClick={(event) => event.stopPropagation()}>
                    <div className="flex flex-wrap align-items-center gap-2">
                        <span className="text-xs text-500">{translate("crm.deal.stage")}</span>
                        <Dropdown value={deal.stage} options={stages.map((stage) => ({value: stage, label: translate(`crm.deal.stage.${stage}`)}))} disabled={!canUpdate} className="text-xs" panelClassName="rtl text-xs" pt={dropdownPt} onChange={(event) => onChangeStage?.(event.value)}/>
                        <small className="text-500">{translate("crm.deal.stage-history-help")}</small>
                    </div>
                    <div className="flex gap-1 w-full" aria-label={translate("crm.deal.stage")}>
                        {stages.map((stage, index) => <span key={stage} className={`flex-1 h-1rem border-round-lg ${index < activeStageIndex ? "bg-primary-200" : index === activeStageIndex ? "bg-primary" : "surface-200"}`}/>) }
                    </div>
                    <div className="flex gap-1 w-full">
                        {stages.map((stage, index) => <small key={stage} className={`flex-1 text-center text-2xs ${index === activeStageIndex ? "text-primary font-bold" : "text-400"}`}>{translate(`crm.deal.stage.${stage}`)}</small>)}
                    </div>
                </div>

                <div className="flex flex-wrap gap-2">
                    {(deal.dealProducts || []).length ? deal.dealProducts.map((item) => <Chip key={item.documentId || item.id} className="text-xs surface-50 border-1 border-100" label={`${item.productName} ${translate("crm.value.multiply-symbol")} ${item.quantity}`}/>) : <span className="text-xs text-500">{translate("crm.deal.no-products")}</span>}
                </div>

                <div className="flex flex-column md:flex-row gap-2">
                    <DealMetric label={translate("crm.deal.product-total")} value={money(deal.productTotal)}/>
                    <DealMetric label={translate("crm.deal.value")} value={money(deal.dealValue)} primary/>
                    <DealMetric label={translate("crm.deal.expected-close")} value={formatDate(deal.expectedCloseDate) || translate("crm.value.empty")}/>
                </div>

                <NextAction action={nextAction} onCreateTask={onCreateTask}/>
            </div>}
        </div>
    </Card>;
}

export function DealDetailsSidebar({
    deal,
    visible,
    onHide,
    timeline = [],
    nextAction,
    canClose = false,
    canReopen = false,
    onWon,
    onLost,
    onReopen,
    onCreateTask,
}) {
    const translate = useTranslate();
    const activities = deal ? timeline.filter((event) => event.deal === deal.documentId || event.deal?.documentId === deal.documentId || event.deal?.id === deal.id) : [];
    const statusKey = deal?.status === "open" ? `crm.deal.stage.${deal?.stage}` : `crm.deal.${deal?.result}`;
    const detailDate = deal?.status === "closed" ? deal.closedAt : deal?.expectedCloseDate || deal?.createdAt;
    const closedDetailLabel = deal?.result === "lost" ? translate("crm.deal.lost-reason") : translate("crm.deal.result");
    const closedDetailValue = deal?.result === "lost" ? (typeof deal?.lostReason === "string" ? deal.lostReason : deal?.lostReason?.title) || deal?.lostNote || translate("crm.value.empty") : translate("crm.deal.won");
    const header = <div className="flex flex-column gap-1">
        <strong className="text-base">{translate("crm.deal.details")}</strong>
        {deal && <small className="text-400">{deal.documentId || `#${deal.id}`}</small>}
    </div>;

    return <Sidebar
        visible={visible}
        onHide={onHide}
        position="left"
        className="w-full md:w-8 lg:w-5"
        header={header}
        closeIcon={<Icon icon="mi mi:close text-xl"/>}
        pt={{
            root: () => ({dir: "rtl"}),
            header: () => ({className: "border-bottom-1 border-100", style: {padding: "18px"}}),
            content: () => ({className: "p-3"}),
        }}
    >
        {deal && <div className="flex flex-column gap-4">
            <div className="flex flex-column gap-3">
                <div className="flex justify-content-between align-items-start gap-3">
                    <strong className="text-xl text-900 line-height-3 white-space-normal">{deal.title}</strong>
                    <Tag rounded severity={statusSeverity(deal)} value={translate(statusKey)}/>
                </div>
                {deal.status === "open" ? <div className="flex flex-wrap gap-2">
                    {canClose && <Button type="button" size="small" severity="success" className="text-xs gap-2" icon={<Icon icon="mi mi:check_circle text-base"/>} label={translate("crm.deal.won")} onClick={() => onWon?.(deal)}/>} 
                    {canClose && <Button type="button" size="small" severity="danger" outlined className="text-xs gap-2" icon={<Icon icon="mi mi:cancel text-base"/>} label={translate("crm.deal.lost")} onClick={() => onLost?.(deal)}/>} 
                    {onCreateTask && <Button type="button" size="small" outlined className="text-xs gap-2" icon={<Icon icon="mi mi:add_task text-base"/>} label={translate("crm.deal.create-task")} onClick={() => onCreateTask(deal)}/>} 
                </div> : <div className="surface-50 border-round-lg p-3 flex flex-column gap-2">
                    <span className={`inline-flex align-items-center gap-2 text-sm font-bold ${deal.result === "won" ? "text-green-600" : "text-red-600"}`}><Icon icon={deal.result === "won" ? "mi mi:check_circle text-lg" : "mi mi:cancel text-lg"}/>{translate(deal.result === "won" ? "crm.deal.status-won" : "crm.deal.status-lost")}</span>
                    {canReopen && <Button type="button" size="small" outlined className="align-self-start text-xs gap-2" icon={<Icon icon="mi mi:refresh text-base"/>} label={translate("crm.deal.reopen")} onClick={() => onReopen?.(deal)}/>} 
                    <small className="text-500">{translate("crm.deal.reopen-history-help")}</small>
                </div>}
            </div>

            <div className="grid m-0">
                <div className="col-12 sm:col-6 p-1"><DealMetric label={translate("crm.deal.value")} value={money(deal.dealValue)} primary/></div>
                <div className="col-12 sm:col-6 p-1"><DealMetric label={translate("crm.deal.stage")} value={translate(`crm.deal.stage.${deal.stage}`)}/></div>
                <div className="col-12 sm:col-6 p-1"><DealMetric label={deal.status === "closed" ? closedDetailLabel : translate("crm.customer.next-action")} value={deal.status === "closed" ? closedDetailValue : nextAction?.title || translate("crm.customer.no-next-action")}/></div>
                <div className="col-12 sm:col-6 p-1"><DealMetric label={translate("crm.deal.date")} value={formatDate(detailDate) || translate("crm.value.empty")}/></div>
            </div>

            <div className="flex flex-column gap-2">
                <strong className="text-sm text-800">{translate("crm.deal.products")}</strong>
                {(deal.dealProducts || []).length ? deal.dealProducts.map((item) => <div key={item.documentId || item.id} className="surface-50 border-1 border-100 border-round-lg p-3 flex justify-content-between align-items-center gap-3 text-xs">
                    <strong className="text-700 white-space-normal">{item.productName}</strong>
                    <span className="text-500 flex-none">{item.quantity} {translate("crm.value.multiply-symbol")} {money(item.unitPrice)}</span>
                </div>) : <span className="surface-50 border-round-lg p-3 text-xs text-500">{translate("crm.deal.no-products")}</span>}
            </div>

            <div className="border-top-1 border-100 mt-1 pt-3 flex flex-column gap-3">
                <strong className="text-sm text-800">{translate("crm.deal.activities")}</strong>
                {activities.length ? <div className="flex flex-column">
                    {activities.map((event, index) => <div key={event.documentId || event.id || index} className="flex gap-3 pb-3">
                        <div className="flex flex-column align-items-center">
                            <span className="w-2rem h-2rem border-circle surface-100 text-primary flex align-items-center justify-content-center flex-none"><Icon icon={`${eventIcon(event.eventType)} text-base`}/></span>
                            {index < activities.length - 1 && <span className="h-full border-left-1 border-200"/>}
                        </div>
                        <div className="flex-1 min-w-0 surface-50 border-round-lg p-2 flex flex-column gap-1">
                            <div className="flex justify-content-between align-items-start gap-2">
                                <strong className="text-xs text-800 white-space-normal">{translate(`crm.timeline.event.${event.eventType}`)}</strong>
                                <span className="text-2xs text-400 flex-none">{formatDate(event.occurredAt, true)}</span>
                            </div>
                            {(event.context?.title || event.context?.description) && <span className="text-xs text-600 white-space-normal">{event.context.title || event.context.description}</span>}
                            <small className="text-500">{memberName(event.actor) || translate("crm.timeline.system")}</small>
                        </div>
                    </div>)}
                </div> : <div className="surface-50 border-round-lg p-4 text-center text-xs text-500">{translate("crm.deal.no-activities")}</div>}
            </div>
        </div>}
    </Sidebar>;
}
